// ─── Stateless sub-components extracted from ShipmentDetailPage ──────────────

import React from "react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import {
  Table, TableBody, TableCell, TableRow,
} from "@/components/ui/table"
import {
  CheckCircle2Icon, XCircleIcon, PackageIcon, MapPinIcon,
  TruckIcon, PencilIcon, Trash2Icon, CheckIcon, AlertTriangleIcon,
  FileIcon, ExternalLinkIcon, CloudSunIcon, CalendarIcon, MoonIcon,
  ShieldCheckIcon, NavigationIcon, ClipboardCheckIcon, MapIcon,
} from "lucide-react"
import type {
  ShipmentDetail, ShipmentDocRow, JourneyMilestone,
  TechCheck, TechCheckItem, TripPlan, TripPlanEquipmentItem, ShipmentDocTypeOption,
} from "./ShipmentDetailTypes"
import {
  WEATHER_CONDITION_META, WEATHER_RATING_META, WEATHER_CONDITION_ORDER,
} from "./ShipmentDetailTypes"
import { fmtDate, fmtTripPlanDate, fmtTripPlanShort } from "./ShipmentDetailUtils"
import { computeStageDurations, shouldCountOpenJourneyStages, totalStayCount, type JourneyStageDef, type StageDuration } from "@cdc-sccr/shared"

// ─── Field ───────────────────────────────────────────────────────────────────

export function Field({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("text-sm", mono && "font-mono")}>
        {value ?? <span className="text-muted-foreground/40">—</span>}
      </dd>
    </div>
  )
}

// ─── SectionCard ──────────────────────────────────────────────────────────────

export function SectionCard({ title, icon, action, children }: {
  title: string
  icon?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b">
        {icon && <span className="text-muted-foreground size-4">{icon}</span>}
        <h2 className="text-sm font-semibold flex-1">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

// ─── TechEquipmentSection ─────────────────────────────────────────────────────

export function TechEquipmentSection({
  items, title, icon,
}: {
  items: TechCheckItem[]
  title: string
  icon: React.ReactNode
}) {
  const okCount  = items.filter(i => i.status === "ok").length
  const obsCount = items.filter(i => i.status === "observado").length
  const pending  = items.filter(i => i.status === null).length

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <span className="text-xs font-semibold tracking-wide">{title}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {okCount  > 0 && <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-bold">{okCount} OK</span>}
          {obsCount > 0 && <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-bold">{obsCount} OBS</span>}
          {pending  > 0 && <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] font-bold">{pending} —</span>}
        </div>
      </div>
      <div className="divide-y divide-border">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <div className={cn(
              "size-5 rounded-full flex items-center justify-center shrink-0",
              item.status === "ok"        ? "bg-emerald-500" :
              item.status === "observado" ? "bg-amber-500"   : "bg-muted",
            )}>
              {item.status === "ok"        && <CheckIcon         className="size-3 text-white" />}
              {item.status === "observado" && <AlertTriangleIcon className="size-3 text-white" />}
              {item.status === null        && <span className="size-1.5 rounded-full bg-muted-foreground/50 block" />}
            </div>
            <span className="flex-1 text-sm">{item.item}</span>
            <span className={cn(
              "text-xs font-semibold",
              item.status === "ok"        ? "text-emerald-600" :
              item.status === "observado" ? "text-amber-600"   : "text-muted-foreground",
            )}>
              {item.status === "ok" ? "OK" : item.status === "observado" ? "OBS" : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── TechCheckModal ───────────────────────────────────────────────────────────

export function TechCheckModal({
  open, onClose, rawCheck, internalId,
}: {
  open: boolean
  onClose: () => void
  rawCheck: Record<string, unknown> | null
  internalId: number
}) {
  if (!rawCheck) return null
  const tc = rawCheck as unknown as TechCheck
  const totalItems   = (tc.truck?.length ?? 0) + (tc.trailer?.length ?? 0)
  const checkedItems = [...(tc.truck ?? []), ...(tc.trailer ?? [])].filter(i => i.status !== null).length

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-row items-center gap-3 px-6 py-4 border-b shrink-0">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-amber-50">
            <ClipboardCheckIcon className="size-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-base font-semibold">Verificación técnica — Carga #{internalId}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {checkedItems}/{totalItems} ítems verificados
              {tc.savedAt && ` · Guardado el ${fmtTripPlanShort(tc.savedAt)}`}
            </p>
          </div>
          <DialogClose className="shrink-0" />
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {(tc.truck?.length ?? 0) > 0 && (
            <TechEquipmentSection items={tc.truck!} title="CAMIÓN" icon={<TruckIcon className="size-3.5" />} />
          )}
          {(tc.trailer?.length ?? 0) > 0 && (
            <TechEquipmentSection items={tc.trailer!} title="ACOPLADO" icon={<PackageIcon className="size-3.5" />} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── EquipmentSection ─────────────────────────────────────────────────────────

export function EquipmentSection({
  items, title, icon,
}: {
  items: TripPlanEquipmentItem[]
  title: string
  icon: React.ReactNode
}) {
  const okCount   = items.filter(i => i.status === "ok").length
  const noOkCount = items.filter(i => i.status === "no_ok").length
  const pending   = items.filter(i => i.status === null).length

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <span className="text-xs font-semibold tracking-wide">{title}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {okCount   > 0 && <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-bold">{okCount} OK</span>}
          {noOkCount > 0 && <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-bold">{noOkCount} No OK</span>}
          {pending   > 0 && <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] font-bold">{pending} —</span>}
        </div>
      </div>
      <div className="divide-y divide-border">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <div className={cn(
              "size-5 rounded-full flex items-center justify-center shrink-0",
              item.status === "ok"     ? "bg-emerald-500" :
              item.status === "no_ok" ? "bg-red-500"     : "bg-muted",
            )}>
              {item.status === "ok"     && <CheckCircle2Icon className="size-3 text-white" />}
              {item.status === "no_ok" && <XCircleIcon      className="size-3 text-white" />}
              {item.status === null    && <span className="size-1.5 rounded-full bg-muted-foreground/50 block" />}
            </div>
            <span className="flex-1 text-sm">{item.item}</span>
            <span className={cn(
              "text-xs font-semibold",
              item.status === "ok"     ? "text-emerald-600" :
              item.status === "no_ok" ? "text-red-600"     : "text-muted-foreground",
            )}>
              {item.status === "ok" ? "OK" : item.status === "no_ok" ? "No OK" : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── WeatherDaySection ────────────────────────────────────────────────────────

export function WeatherDaySection({ wday }: {
  wday: { day: number; date: string; ratings: Record<string, string> }
}) {
  const hasBad    = Object.values(wday.ratings).some(r => r === "bad")
  const dateLabel = new Date(wday.date).toLocaleDateString("es-AR", {
    weekday: "short", day: "numeric", month: "short",
  })
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="size-6 rounded-full bg-foreground flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-background">{wday.day}</span>
        </div>
        <span className="text-sm font-semibold">Día {wday.day}</span>
        <span className="text-xs text-muted-foreground">{dateLabel}</span>
        {hasBad && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-bold">
            ⚠️ Adversas
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {WEATHER_CONDITION_ORDER.map(condId => {
          const meta   = WEATHER_CONDITION_META[condId]
          const rating = wday.ratings[condId] ?? "regular"
          const style  = WEATHER_RATING_META[rating] ?? WEATHER_RATING_META["regular"]!
          return (
            <div key={condId} className={cn("flex flex-col items-center gap-1 rounded-lg border px-2 py-2", style.className)}>
              <span className="text-lg leading-none">{meta?.emoji}</span>
              <span className="text-[9px] font-semibold text-center leading-tight uppercase text-muted-foreground">{meta?.label}</span>
              <span className={cn("text-[9px] font-bold rounded-full px-1.5 py-0.5", style.badgeClassName)}>{style.label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── TripPlanModal ────────────────────────────────────────────────────────────

export function TripPlanModal({
  open, onClose, rawPlan, internalId,
}: {
  open: boolean
  onClose: () => void
  rawPlan: Record<string, unknown> | null
  internalId: number
}) {
  if (!rawPlan) return null
  const p = rawPlan as unknown as TripPlan

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-2xl p-0 gap-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="flex-row items-center gap-3 px-6 py-4 border-b shrink-0">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50">
            <MapIcon className="size-4 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-base font-semibold">Plan de viaje — Carga #{internalId}</DialogTitle>
            {p.savedAt && (
              <p className="text-xs text-muted-foreground mt-0.5">Guardado el {fmtTripPlanShort(p.savedAt)}</p>
            )}
          </div>
          <DialogClose className="shrink-0" />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Fecha de salida</h3>
            </div>
            <p className="text-sm font-medium">{fmtTripPlanDate(p.departureDate)}</p>
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
              <MoonIcon className="size-4 text-muted-foreground" />
              <span className="text-sm">
                <span className="font-semibold">{p.restHours ?? 0}</span>
                {" "}hora{(p.restHours ?? 0) === 1 ? "" : "s"} de descanso previo
              </span>
            </div>
          </div>

          {p.equipment && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="size-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Verificación de equipos</h3>
              </div>
              {(p.equipment.truck?.length ?? 0) > 0 && (
                <EquipmentSection items={p.equipment.truck} title="CAMIÓN" icon={<TruckIcon className="size-3.5" />} />
              )}
              {(p.equipment.trailer?.length ?? 0) > 0 && (
                <EquipmentSection items={p.equipment.trailer} title="REMOLQUE / SEMIRREMOLQUE" icon={<PackageIcon className="size-3.5" />} />
              )}
            </div>
          )}

          {p.route ? (
            <div className="rounded-lg border bg-card p-4 space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <NavigationIcon className="size-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Ruta planificada</h3>
              </div>
              <div className="rounded-lg bg-foreground px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-background">{p.route.origen} → {p.route.destino}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold",
                      p.route.tipo_movil === "gnc" ? "bg-green-800/40 text-green-300" : "bg-white/10 text-white/70",
                    )}>
                      {p.route.tipo_movil === "gnc" ? "GNC" : "Diesel"}
                    </span>
                    <span className="text-[11px] text-white/50">
                      {Object.keys(p.route.paradas).length} parada{Object.keys(p.route.paradas).length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {Object.entries(p.route.paradas)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([num, options]) => (
                    <div key={num}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="size-5 rounded-full bg-foreground flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-background">{num}</span>
                        </div>
                        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Parada {num}</span>
                      </div>
                      <div className="space-y-1.5">
                        {(options as string[]).map((opt, i) => {
                          const isRest = p.route?.descansos?.[`${num}:${opt}`] === true
                          return (
                            <div key={i} className={cn(
                              "flex items-center gap-2 rounded-lg px-3 py-2 border",
                              isRest ? "bg-indigo-50 border-indigo-200" : "bg-green-50 border-green-200",
                            )}>
                              <MapPinIcon className={cn("size-3.5 shrink-0", isRest ? "text-indigo-500" : "text-emerald-600")} />
                              <span className={cn("text-sm font-medium flex-1", isRest ? "text-indigo-800" : "text-emerald-800")}>{opt}</span>
                              {isRest && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[10px] font-bold">
                                  🌙 Descanso
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
              <MapPinIcon className="size-4 text-amber-600 shrink-0" />
              <span className="text-sm text-amber-800">Sin ruta de paradas planificada</span>
            </div>
          )}

          {p.weatherConditions && p.weatherConditions.length > 0 && (
            <div className="rounded-lg border bg-card p-4 space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <CloudSunIcon className="size-4 text-muted-foreground" />
                <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Condiciones del viaje</h3>
              </div>
              {p.weatherConditions.map((wday) => (
                <WeatherDaySection key={wday.day} wday={wday} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── JourneyTimeline ──────────────────────────────────────────────────────────

export function JourneyTimeline({
  milestones, stages = [], shipment, onMilestoneClick,
}: {
  milestones: JourneyMilestone[]
  stages?: JourneyStageDef[] | undefined
  shipment: ShipmentDetail
  onMilestoneClick: ((m: JourneyMilestone, currentValue: string | null) => void) | undefined
}) {
  const lastCompletedIdx = milestones.reduce(
    (acc, m, i) => (shipment[m.key] ? i : acc), -1,
  )
  const LABEL_H = "h-12"
  // Dense timelines (international) don't fit horizontal labels: all labels go above, rotated.
  const diagonal = milestones.length > 8
  const stageDurations = computeStageDurations(stages, shipment, {
    countOpenStages: shouldCountOpenJourneyStages(shipment.status),
  })

  return (
    <div className={cn("w-full", diagonal && "pr-14")}>
      <div className="flex">
        {milestones.map((m, i) => {
          const value       = shipment[m.key] as string | null
          const isCompleted = !!value
          const isActive    = i === lastCompletedIdx
          const isLast      = i === milestones.length - 1
          const prevDone    = i > 0 && !!shipment[milestones[i - 1]!.key]
          const above       = diagonal || i % 2 === 0

          const labelBlock = diagonal ? (
            <div className="absolute bottom-1 left-1/2 origin-bottom-left -rotate-[40deg] whitespace-nowrap leading-tight">
              <p className="text-[10px] text-muted-foreground group-hover:text-foreground transition-colors">{m.label}</p>
              <p className={cn(
                "text-[10px] tabular-nums font-medium",
                value ? "text-foreground" : "text-muted-foreground/40",
              )}>
                {value ? fmtDate(value) : "—"}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-0.5 px-0.5">
              <p className="text-[10px] leading-tight text-center text-muted-foreground">{m.label}</p>
              <p className={cn(
                "text-[10px] tabular-nums text-center font-medium",
                value ? "text-foreground" : "text-muted-foreground/40",
              )}>
                {value ? fmtDate(value) : "—"}
              </p>
            </div>
          )

          return (
            <div
              key={m.key}
              className={cn("flex flex-col items-center flex-1 min-w-0", onMilestoneClick && "cursor-pointer group")}
              onClick={() => onMilestoneClick?.(m, value)}
            >
              <div className={cn(
                diagonal ? "relative h-28" : LABEL_H,
                "flex flex-col items-center justify-end pb-1 w-full",
              )}>
                {above && labelBlock}
              </div>
              <div className="relative flex items-center w-full h-4">
                <div className={cn(
                  "absolute left-0 top-1/2 -translate-y-1/2 h-0.5 w-1/2",
                  i === 0 ? "bg-transparent" : prevDone ? "bg-primary" : "bg-muted-foreground/20",
                )} />
                <div className={cn(
                  "absolute right-0 top-1/2 -translate-y-1/2 h-0.5 w-1/2",
                  isLast ? "bg-transparent" : isCompleted ? "bg-primary" : "bg-muted-foreground/20",
                )} />
                <div className={cn(
                  "relative z-10 mx-auto size-2.5 rounded-full border-2 shrink-0",
                  isCompleted ? "bg-primary border-primary" : "border-muted-foreground/30 bg-background",
                  isActive && "ring-2 ring-primary ring-offset-1 ring-offset-card",
                )} />
              </div>
              {!diagonal && (
                <div className={cn(LABEL_H, "flex flex-col items-center justify-start pt-1 w-full")}>
                  {!above && labelBlock}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {stageDurations.length > 0 && (
        <div className="relative h-10 mt-1">
          {stageDurations.map(({ stage, days, inProgress }) => {
            const fromIdx = milestones.findIndex(m => m.key === stage.from)
            const toIdx   = milestones.findIndex(m => m.key === stage.to)
            if (fromIdx < 0 || toIdx <= fromIdx) return null
            const columnPct = 100 / milestones.length
            return (
              <div
                key={stage.key}
                className="absolute top-0 flex flex-col items-center"
                style={{ left: `${(fromIdx + 0.5) * columnPct}%`, width: `${(toIdx - fromIdx) * columnPct}%` }}
                title={`${stage.label}: ${days ?? "—"} ${days === 1 ? "día" : "días"}${inProgress ? " (en curso)" : ""}`}
              >
                <div className={cn(
                  "h-1.5 w-full rounded-b border-x border-b",
                  days === null ? "border-muted-foreground/20" : inProgress ? "border-amber-400" : "border-primary/50",
                )} />
                <span className={cn(
                  "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums whitespace-nowrap",
                  days === null ? "bg-muted text-muted-foreground/60"
                    : inProgress ? "bg-amber-100 text-amber-700"
                    : "bg-primary/10 text-primary",
                )}>
                  {stage.label} · {days === null ? "—" : `${days} ${days === 1 ? "día" : "días"}`}
                  {inProgress && <span className="font-normal">(en curso)</span>}
                </span>
              </div>
            )
          })}
        </div>
      )}
      {stageDurations.length > 0 && <StaySummary durations={stageDurations} />}
    </div>
  )
}

function StaySummary({ durations }: { durations: StageDuration[] }) {
  const totalStays = totalStayCount(durations)

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-t pt-3 text-xs">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Estadías</span>
      {durations.map(({ stage, days, stays, inProgress }) => (
        <span key={stage.key} className="flex items-baseline gap-1.5">
          <span className="text-muted-foreground">{stage.label}</span>
          <span className={cn(
            "font-semibold tabular-nums",
            days === null ? "text-muted-foreground/40" : inProgress ? "text-amber-700" : "text-foreground",
          )}>
            {days === null ? "—" : stays}
          </span>
        </span>
      ))}
      <span className="ml-auto flex items-baseline gap-1.5 tabular-nums">
        <span className="text-muted-foreground">Total</span>
        <span className="text-sm font-semibold">{totalStays}</span>
      </span>
    </div>
  )
}

// ─── DocRow ───────────────────────────────────────────────────────────────────

export function DocRow({
  doc, editing, onDelete, onSaveDoc, docTypeOptions,
}: {
  doc: ShipmentDocRow
  editing: boolean
  onDelete: () => Promise<void>
  /** `docTypeId` is undefined when the type did not change. */
  onSaveDoc: (notes: string | null, docTypeId: string | undefined, letter: string | null) => Promise<void>
  docTypeOptions: ShipmentDocTypeOption[]
}) {
  const [rowEditing, setRowEditing] = React.useState(false)
  const [note,       setNote]       = React.useState(doc.notes ?? "")
  const [docTypeId,  setDocTypeId]  = React.useState(doc.docTypeId)
  const [letter,     setLetter]     = React.useState(doc.letter ?? "")
  const [saving,     setSaving]     = React.useState(false)
  const [deleting,   setDeleting]   = React.useState(false)

  // The current type may have been soft-deleted from the catalog; keep it selectable.
  const selectOptions = React.useMemo(() => (
    docTypeOptions.some(option => option.id === doc.docTypeId)
      ? docTypeOptions
      : [{ id: doc.docTypeId, code: doc.docTypeCode, name: doc.docTypeName, control: doc.docTypeControl }, ...docTypeOptions]
  ), [docTypeOptions, doc.docTypeId, doc.docTypeCode, doc.docTypeName, doc.docTypeControl])

  async function handleSave() {
    setSaving(true)
    try {
      await onSaveDoc(
        note.trim() || null,
        docTypeId !== doc.docTypeId ? docTypeId : undefined,
        letter.trim().toUpperCase() || null,
      )
      setRowEditing(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar el documento")
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setNote(doc.notes ?? "")
    setDocTypeId(doc.docTypeId)
    setLetter(doc.letter ?? "")
    setRowEditing(false)
  }

  async function handleDelete() {
    setDeleting(true)
    await onDelete()
    setDeleting(false)
  }

  return (
    <TableRow>
      <TableCell>
        <a
          href={doc.signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-blue-600 hover:underline font-medium text-sm"
        >
          <FileIcon className="size-3.5 shrink-0" />
          <span className="truncate max-w-[160px]">{doc.fileName ?? "Documento"}</span>
          <ExternalLinkIcon className="size-3 shrink-0" />
        </a>
      </TableCell>
      <TableCell className="w-40">
        {editing && rowEditing ? (
          <select
            value={docTypeId}
            onChange={e => {
              setDocTypeId(e.target.value)
              setLetter(selectOptions.find(option => option.id === e.target.value)?.control ?? "")
            }}
            className="h-7 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
          >
            {selectOptions.map(option => (
              <option key={option.id} value={option.id}>{option.code} — {option.name}</option>
            ))}
          </select>
        ) : (
          <span className="text-[10px] px-1.5 py-0 rounded-full font-medium bg-sky-100 text-sky-700">
            {doc.docTypeCode} — {doc.docTypeName}
          </span>
        )}
      </TableCell>
      <TableCell className="w-16">
        {editing && rowEditing ? (
          <Input
            aria-label="Letra"
            value={letter}
            onChange={e => setLetter(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase())}
            maxLength={1}
            className="h-7 w-12 text-sm text-center font-mono uppercase"
          />
        ) : doc.letter ? (
          <span className="font-mono text-xs font-medium">{doc.letter}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="max-w-[220px]">
        {editing && rowEditing ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Sin comentario"
              className="h-7 text-sm"
              autoFocus
            />
            <Button size="sm" variant="default" className="h-7 px-2 text-xs" disabled={saving} onClick={() => void handleSave()}>
              {saving ? "…" : "OK"}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCancel}>✕</Button>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground line-clamp-2">{doc.notes ?? "—"}</span>
        )}
      </TableCell>
      <TableCell className="text-sm">{doc.uploaderFullName ?? "—"}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(doc.createdAt).toLocaleDateString("es-AR", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })}
      </TableCell>
      {editing && (
        <TableCell>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost" size="icon"
              className="size-7 text-muted-foreground hover:text-foreground"
              onClick={() => { setNote(doc.notes ?? ""); setDocTypeId(doc.docTypeId); setLetter(doc.letter ?? ""); setRowEditing(true) }}
              title="Editar tipo, letra y comentario"
            >
              <PencilIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost" size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              disabled={deleting}
              onClick={() => void handleDelete()}
              title="Eliminar documento"
            >
              <Trash2Icon className="size-3.5" />
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}

// ─── FlagBadge ────────────────────────────────────────────────────────────────

export function FlagBadge({ value, label }: { value: boolean; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {value
        ? <CheckCircle2Icon className="size-4 text-emerald-500 shrink-0" />
        : <XCircleIcon      className="size-4 text-muted-foreground/40 shrink-0" />}
      <span className={cn("text-xs", value ? "text-foreground" : "text-muted-foreground/60")}>
        {label}
      </span>
    </div>
  )
}
